/**
 * Page publique de vérification d'un relevé via son code.
 * Accessible sans authentification.
 */
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { PortailLayout } from '../PortailLayout';
import { portailApi } from '../services/portailApi';

interface VerifResult {
  valide: boolean;
  code: string;
  matricule: string;
  nom_complet: string;
  classe: string;
  semestre: number;
  annee_academique: string;
  moyenne_semestre: string | null;
  semestre_valide: boolean | null;
  // Notes d'examens masquées pour ce semestre : moyenne et décision non communiquées
  resultats_masques?: boolean;
  date_generation: string;
  nb_consultations: number;
}

export default function Verifier() {
  const { code } = useParams<{ code: string }>();
  const [result, setResult] = useState<VerifResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!code) {
      setError('Code manquant');
      setLoading(false);
      return;
    }
    portailApi.verifierCodePublic(code)
      .then((r) => setResult(r as VerifResult))
      .catch((e: any) => setError(e.message || 'Code invalide ou introuvable'))
      .finally(() => setLoading(false));
  }, [code]);

  return (
    <PortailLayout showHeader title="Vérification d'un relevé">
      <div className="space-y-4 mt-2">
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 text-sm text-blue-800 dark:text-blue-200">
          Cette page permet de vérifier l'authenticité d'un relevé téléchargé depuis le portail étudiant 2i.
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow border border-gray-200 dark:border-gray-700 p-6">
          {loading ? (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
              <p className="text-gray-600 dark:text-gray-300 mt-3">Vérification en cours...</p>
            </div>
          ) : error || !result ? (
            <div className="text-center py-8">
              <div className="text-5xl mb-3">❌</div>
              <h3 className="font-bold text-red-700 dark:text-red-400">Code invalide</h3>
              <p className="text-sm text-gray-600 dark:text-gray-300 mt-2">
                {error || 'Aucun relevé ne correspond à ce code.'}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-3 font-mono">
                Code recherché : {code}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="text-center py-2">
                <div className="text-5xl mb-2">✓</div>
                <h3 className="font-bold text-green-700 dark:text-green-400 text-lg">Relevé authentique</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Code validé : <span className="font-mono">{result.code}</span>
                </p>
              </div>

              <div className="border-t border-gray-200 dark:border-gray-700 pt-4 space-y-3">
                <div>
                  <div className="text-xs uppercase text-gray-500 dark:text-gray-400 tracking-wide">Étudiant</div>
                  <div className="font-bold text-gray-900 dark:text-white">{result.nom_complet}</div>
                  <div className="text-sm text-gray-600 dark:text-gray-300 font-mono">{result.matricule}</div>
                </div>

                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <div className="text-xs uppercase text-gray-500 dark:text-gray-400 tracking-wide">Classe</div>
                    <div className="font-medium text-gray-900 dark:text-white">{result.classe || '—'}</div>
                  </div>
                  <div>
                    <div className="text-xs uppercase text-gray-500 dark:text-gray-400 tracking-wide">Année</div>
                    <div className="font-medium text-gray-900 dark:text-white">{result.annee_academique}</div>
                  </div>
                  <div>
                    <div className="text-xs uppercase text-gray-500 dark:text-gray-400 tracking-wide">Semestre</div>
                    <div className="font-medium text-gray-900 dark:text-white">S{result.semestre}</div>
                  </div>
                  {!result.resultats_masques && (
                    <div>
                      <div className="text-xs uppercase text-gray-500 dark:text-gray-400 tracking-wide">Moyenne</div>
                      <div className="font-medium text-gray-900 dark:text-white">
                        {result.moyenne_semestre || '—'}
                      </div>
                    </div>
                  )}
                </div>

                {result.resultats_masques ? (
                  <div className="p-3 rounded-lg text-sm bg-gray-50 dark:bg-gray-900/40 text-gray-700 dark:text-gray-300">
                    Résultats de ce semestre en attente de publication.
                  </div>
                ) : (
                  <div className={`p-3 rounded-lg text-sm font-semibold ${
                    result.semestre_valide
                      ? 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-300'
                      : 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-300'
                  }`}>
                    {result.semestre_valide ? '✓ Semestre validé' : '✗ Semestre non validé'}
                  </div>
                )}

                <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-3 text-xs text-orange-800 dark:text-orange-200">
                  ⚠ Ce relevé est <strong>provisoire</strong> et susceptible d'être modifié après délibération définitive.
                </div>

                <div className="text-xs text-gray-500 dark:text-gray-400 border-t border-gray-200 dark:border-gray-700 pt-2">
                  Code généré le {new Date(result.date_generation).toLocaleString('fr-FR')}<br />
                  Vérifié {result.nb_consultations} fois
                </div>
              </div>
            </div>
          )}
        </div>

        <Link
          to="/portail/login"
          className="block text-center text-sm text-blue-600 dark:text-blue-400 hover:underline"
        >
          → Accéder au portail étudiant
        </Link>
      </div>
    </PortailLayout>
  );
}
